import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const OtherIntegrationModal = NiceModal.create(({ id }) => {
	const dispatch = useDispatch();

	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const defaultColSpan = {
		md: 6
	};

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().required(),
					keyString: yup.string().nullable(),
					type: yup.string().required(),
					description: yup.string().required(),
					serviceURL: yup.string().nullable(),
					fieldNames: yup.string().nullable(),
					logo: yup.string().required()
				})
				.required(),
		[]
	);
	const fillForm = useCallback(async () => {
		if (!id) {
			return {
				name: null,
				keyString: null,
				type: null,
				description: null,
				serviceURL: null,
				fieldNames: null
			};
		}

		try {
			const response = await axiosPrivate.post(ADMIN.OTHER_INTEGRATIONS.GET_OTHER_INTEGRATION_BY_ID, {
				ID: id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				name: data.name,
				keyString: data.keyString,
				type: data.type,
				description: data.description,
				serviceURL: data.serviceURL,
				fieldNames: data.fieldNames,
				logo: data.logo
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading },
		watch,
		setValue
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (data) => {
		const URL = id
			? ADMIN.OTHER_INTEGRATIONS.UPDATE_OTHER_INTEGRATIONS
			: ADMIN.OTHER_INTEGRATIONS.ADD_OTHER_INTEGRATIONS;

		try {
			const response = await axiosPrivate.post(URL, {
				...data,
				...(id && { ID: id })
			});

			const { status } = response;

			if (status !== 200) {
				showError();
				return;
			}

			showSuccess();
			dispatch(updateDynamicListData(DYNAMICLISTS.OTHER_INTEGRATIONS_ADMIN));
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			handleCloseModal();
		}
	};

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	return (
		<Modal fullwidth maxWidth="medium" className="modal-otherIntegrations">
			<ModalTitle>
				<Typography variant="p1">addOtherIntegrations</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="name" isLoading={isLoading} required>
						<StringInput name="name" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="keyString" isLoading={isLoading}>
						<StringInput name="keyString" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="type" isLoading={isLoading} required>
						<EnumSelect
							name="type"
							enumName={ENUMS.OTHER_INTEGRATION_TYPES}
							value={watch('type')}
							setValue={(value) => {
								setValue('type', value, { shouldValidate: true, shouldDirty: true });
							}}
							searchable={false}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="description" isLoading={isLoading} required>
						<StringInput name="description" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="serviceURL" isLoading={isLoading}>
						<StringInput name="serviceURL" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="fieldNames" isLoading={isLoading}>
						<StringInput name="fieldNames" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="logo" isLoading={isLoading} required>
						<StringInput name="logo" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="logoPreview" isLoading={isLoading}>
						{watch('logo') && <Image url={watch('logo')} width="10rem" height="10rem" alt="logo" />}
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OtherIntegrationModal;
